import type { ReactNode } from 'react';
import {
  coerceBhConnectionCount,
  coerceBhConnectionPercent,
  type ConnectionsPcpCareTrendPoint,
} from '@act/shared/data-transfer-objects';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { useReportChartDimensions } from '../../hooks/useReportChartDimensions.hook';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import { body, CORAL, display, foreground, muted, NAVY } from './tokens';

const PCP_TREND_CHART_HEIGHT = 280;

const cardStyle = {
  background: '#fff',
  border: '0.5px solid var(--reema-midnight-25)',
  borderRadius: 24,
  padding: '2.5rem',
} as const;
const DISCONNECTED_BAR_MAX_WIDTH = 820;

const disconnectedBarWidthPct = (value: number, total: number): number =>
  total > 0 ? (value / total) * 100 : 0;

const readTrend = (data: Record<string, unknown>): ConnectionsPcpCareTrendPoint[] => {
  if (!Array.isArray(data.pcpCareTrend)) {
    return [];
  }
  return data.pcpCareTrend as ConnectionsPcpCareTrendPoint[];
};

const readDisconnectedMetrics = (data: Record<string, unknown>) => ({
  noPreEngPcpVisit: coerceBhConnectionCount(
    data.noPreEngPcpVisit ?? data.membersNoPcpOverYear,
  ),
  eligibleCostPcpConnected: coerceBhConnectionCount(data.eligibleCostPcpConnected),
  eligiblePct: coerceBhConnectionPercent(
    data.eligibleCostPcpPct ?? data.pctDisconnectedNowInPcp,
  ),
});

export interface ConnectionsPrimaryCarePageTwoProps {
  /** Resolved section snapshot. */
  data: Record<string, unknown>;
  /** Optional callout/insight tail (rendered as direct children of the page-two shell). */
  tail?: ReactNode;
}

/**
 * Page-two print shell: disconnected bar chart card and optional insight tail.
 * @param props - Snapshot data and optional tail blocks from {@link ReportSectionItemView}.
 */
export const ConnectionsPrimaryCarePageTwo = ({
  data,
  tail,
}: ConnectionsPrimaryCarePageTwoProps) => {
  const { noPreEngPcpVisit, eligibleCostPcpConnected, eligiblePct } =
    readDisconnectedMetrics(data);

  if (noPreEngPcpVisit <= 0 && tail == null) {
    return null;
  }

  return (
    <div className="connections-pcp-page-two-tail">
      <div className="connections-pcp-page-two-body max-w-5xl mx-auto px-6 w-full">
        <div className="connections-pcp-print-page-lead" aria-hidden="true" />
        {noPreEngPcpVisit > 0 ? (
          <ReportMockFieldFrame data={data} field="noPreEngPcpVisit" className="mb-10">
            <div
              className="connections-pcp-disconnected-card"
              style={{ ...cardStyle, marginBottom: '4rem' }}
            >
              <h3
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: 28,
                  color: foreground,
                  margin: '0 0 0.25rem',
                  lineHeight: 1.2,
                }}
              >
                From disconnected to in care
              </h3>
              <p
                style={{
                  fontFamily: body,
                  fontSize: 13,
                  color: muted,
                  margin: '0 0 2rem',
                  lineHeight: 1.5,
                }}
              >
                Engaged members with no PCP visit in the prior 12 months, and the share now in
                care.
              </p>
              <div className="space-y-8">
                <div>
                  <div className="text-sm mb-2" style={{ color: muted }}>
                    Engaged members with no PCP visit in last 12 months
                  </div>
                  <div className="flex items-center gap-3">
                    <div style={{ width: '100%', maxWidth: DISCONNECTED_BAR_MAX_WIDTH }}>
                      <div className="h-8 rounded" style={{ width: '100%', background: NAVY }} />
                    </div>
                    <div className="text-sm font-medium shrink-0" style={{ color: foreground }}>
                      {noPreEngPcpVisit.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-sm mb-2" style={{ color: muted }}>
                    Now seeing a PCP after Reema engagement
                  </div>
                  <div className="flex items-center gap-3">
                    <div style={{ width: '100%', maxWidth: DISCONNECTED_BAR_MAX_WIDTH }}>
                      <div
                        className="h-8 rounded"
                        style={{
                          width: `${disconnectedBarWidthPct(eligibleCostPcpConnected, noPreEngPcpVisit)}%`,
                          background: CORAL,
                          minWidth: eligibleCostPcpConnected > 0 ? 24 : 0,
                        }}
                      />
                    </div>
                    <div className="text-sm font-medium shrink-0" style={{ color: foreground }}>
                      {eligibleCostPcpConnected.toLocaleString()} ({eligiblePct}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ReportMockFieldFrame>
        ) : null}
      </div>
      {tail != null ? (
        <>
          <div className="connections-pcp-page-two-flex-fill" aria-hidden="true" />
          {tail}
        </>
      ) : null}
    </div>
  );
};

/**
 * Connections to Primary Care: PCP connection metrics, trend, and disconnected-member bars.
 * @param props - Resolved section snapshot.
 */
const ConnectionsPrimaryCare = ({
  data,
}: ReportHostedSectionProps) => {
  const printStatic = useReportsPrintStaticLayout();
  const { containerRef: pcpTrendChartRef, dimensions: pcpTrendChartDimensions } =
    useReportChartDimensions(printStatic ? 170 : PCP_TREND_CHART_HEIGHT);
  const membersSeenByPcp = coerceBhConnectionCount(
    data.membersSeenByPcp ?? data.newPcpConnections,
  );
  const pctEngagedConnectedPcp = coerceBhConnectionPercent(
    data.pctEngagedConnectedPcp ?? data.connectionRate,
  );
  const pctDisconnectedNowInPcp = coerceBhConnectionPercent(
    data.pctDisconnectedNowInPcp ?? data.eligibleCostPcpPct,
  );
  const { noPreEngPcpVisit, eligibleCostPcpConnected, eligiblePct } =
    readDisconnectedMetrics(data);
  const trend = readTrend(data);

  return (
    <HostedReportSectionLayout
      sectionDomId="connections-primary-care"
      className="section--white report-hosted-section-body"
      style={{ fontFamily: body }}
      banner={
        <SectionBanner
          title="Connections to Primary Care"
          iconHref="#icon_medical_health"
        />
      }
    >
      <SectionReveal>
        <ReportMockFieldFrame data={data} field="membersSeenByPcp">
          <div
            className="stat-grid stat-grid--4"
            style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '4rem' }}
          >
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">
                {membersSeenByPcp.toLocaleString()}
              </div>
              <div className="stat-label">Members seen by PCP since engaging</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">{pctEngagedConnectedPcp}%</div>
              <div className="stat-label">Of engaged members now connected to PCP</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">{pctDisconnectedNowInPcp}%</div>
              <div className="stat-label">
                Of previously-disconnected members now in PCP care
              </div>
            </div>
          </div>
        </ReportMockFieldFrame>

        {trend.length > 0 ? (
          <ReportMockFieldFrame data={data} field="pcpCareTrend" className="mb-10">
            <div className="connections-pcp-trend-card" style={{ ...cardStyle, marginBottom: '4rem' }}>
              <h3
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: 18,
                  color: foreground,
                  margin: '0 0 0.25rem',
                  lineHeight: 1.2,
                }}
              >
                Members engaged in primary care
              </h3>
              <p
                style={{
                  fontFamily: body,
                  fontSize: 13,
                  color: muted,
                  margin: '0 0 1.5rem',
                  lineHeight: 1.5,
                }}
              >
                Total active members in primary care, last 6 months.
              </p>
              <div
                ref={pcpTrendChartRef}
                className="connections-pcp-trend-chart report-chart-host"
                style={{
                  width: '100%',
                  minWidth: 0,
                  height: printStatic ? 170 : PCP_TREND_CHART_HEIGHT,
                }}
              >
                {pcpTrendChartDimensions ? (
                  <LineChart
                    width={pcpTrendChartDimensions.width}
                    height={pcpTrendChartDimensions.height}
                    data={trend}
                    margin={
                      printStatic
                        ? { top: 20, right: 4, left: 0, bottom: 4 }
                        : { top: 24, right: 8, left: 0, bottom: 8 }
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      scale="point"
                      padding={{ left: 0, right: 0 }}
                      tick={{ fontSize: 11, fill: muted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: muted }}
                      width={printStatic ? 40 : 48}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid #E8E0D6',
                        fontSize: 13,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={CORAL}
                      strokeWidth={2.5}
                      dot={{ r: 5, fill: CORAL, strokeWidth: 0 }}
                      activeDot={{ r: 6 }}
                    >
                      <LabelList
                        dataKey="value"
                        position="top"
                        offset={12}
                        style={{
                          fill: foreground,
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: "'Lora', serif",
                        }}
                      />
                    </Line>
                  </LineChart>
                ) : null}
              </div>
            </div>
          </ReportMockFieldFrame>
        ) : null}

        {noPreEngPcpVisit > 0 ? (
          <ReportMockFieldFrame
            data={data}
            field="noPreEngPcpVisit"
            className="connections-pcp-disconnected-intro mb-10"
          >
            <div style={{ marginBottom: '2.5rem' }}>
              <h3
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: 'var(--reema-text-h2)',
                  color: foreground,
                  margin: '0 0 1rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                The members who hadn&apos;t seen a PCP in over a year.
              </h3>
              <p
                style={{
                  fontFamily: body,
                  fontSize: 'var(--reema-text-body-lg)',
                  color: muted,
                  maxWidth: '48rem',
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {noPreEngPcpVisit.toLocaleString()} of the engaged members hadn&apos;t visited a
                primary care provider in the last 12 months. These are the members least likely to
                navigate the healthcare system on their own.{' '}
                {eligibleCostPcpConnected.toLocaleString()} of them have initiated primary care
                during the Reema program, a {eligiblePct}% activation rate in the hardest-to-reach
                segment of your population.
              </p>
            </div>
          </ReportMockFieldFrame>
        ) : null}
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default ConnectionsPrimaryCare;
